import { memo, useMemo } from 'react';
import { useQuizList } from '@/views/build/components/useQuizList';
import { useBuildInfoContext } from '@/views/build/context';
import { useAiBuilderBuilderContext } from '../hooks';
import { type AiBuilderMessage, useAiBuilderChatIsGeneratingSelector, useAiBuilderChatMessagesSelector } from '../store';
import { getAiBuilderQuestionState } from '../utils/aiBuilderWelcomePolicy';
import { AiBuilderAssistantMessage } from './AiBuilderAssistantMessage';
import { AiBuilderChoiceMessage } from './AiBuilderChoiceMessage';
import { AiBuilderConfirmMessage } from './AiBuilderConfirmMessage';
import { AiBuilderIntroMessage } from './AiBuilderIntroMessage';
import { AiBuilderProgressMessage } from './AiBuilderProgressMessage';
import { AiBuilderSystemMessage } from './AiBuilderSystemMessage';
import { AiBuilderUserMessage } from './AiBuilderUserMessage';
export const AiBuilderMessageList = () => {
  const builderContext = useAiBuilderBuilderContext();
  const {
    action
  } = useBuildInfoContext();
  const {
    quizList
  } = useQuizList();
  const {
    conversationEntryIntent,
    conversationQuestionState,
    messages
  } = useAiBuilderChatMessagesSelector();
  const isGenerating = useAiBuilderChatIsGeneratingSelector();
  const questionState = conversationQuestionState ?? getAiBuilderQuestionState({
    action,
    questionCount: quizList.length
  });
  const renderRows = useMemo(() => createAiBuilderMessageRenderRows(messages), [messages]);
  // 실제로 스트리밍 중인 메시지에만 타이핑 효과를 켜기 위한 대상 id.
  // 턴 진행 중일 때, 가장 최근 user 메시지 이후에 나온 마지막 assistant 가
  // 스트리밍 대상이다. 완료 시 isGenerating 이 false 가 되어 대상이 사라진다
  // (→ 완성 메시지는 즉시 표시).
  const streamingAssistantId = useMemo(() => isGenerating ? findStreamingAssistantMessageId(messages) : null, [isGenerating, messages]);
  return <>
      <AiBuilderIntroMessage builderContext={builderContext} entryIntent={conversationEntryIntent ?? 'chat'} questionState={questionState} data-sentry-element="AiBuilderIntroMessage" data-sentry-source-file="AiBuilderMessageList.tsx" />
      {renderRows.map(row => <AiBuilderMessageRow key={row.id} {...row} streamingAssistantId={streamingAssistantId} />)}
    </>;
};

// 가장 최근 user 메시지 이후에 나온 마지막 assistant 메시지 id 를 찾는다.
// 뒤에서부터 훑다가 user 메시지를 먼저 만나면(= 새 턴은 시작됐으나 아직 응답
// assistant 가 없음) null 을 반환해, 직전(완료된) assistant 가 새 턴의 thinking
// 단계에서 스트리밍 대상으로 잘못 잡혀 재타이핑되는 것을 막는다.
export const findStreamingAssistantMessageId = (messages: AiBuilderMessage[]): string | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.kind === 'assistant') return message.id;
    if (message.kind === 'user') return null;
  }
  return null;
};
type AiBuilderAssistantMessageRenderRow = {
  id: string;
  type: 'assistantGroup';
  variant: 'assistantWithProgress';
  assistantMessage: Extract<AiBuilderMessage, {
    kind: 'assistant';
  }>;
  progressMessage: Extract<AiBuilderMessage, {
    kind: 'progress';
  }>;
} | {
  id: string;
  type: 'assistantGroup';
  variant: 'progressOnly';
  progressMessage: Extract<AiBuilderMessage, {
    kind: 'progress';
  }>;
} | {
  id: string;
  type: 'assistantGroup';
  variant: 'assistantOnly';
  assistantMessage: Extract<AiBuilderMessage, {
    kind: 'assistant';
  }>;
};
type AiBuilderMessageRenderRow = AiBuilderAssistantMessageRenderRow | {
  id: string;
  type: 'standaloneMessage';
  message: Exclude<AiBuilderMessage, {
    kind: 'assistant';
  } | {
    kind: 'progress';
  }>;
};
export const createAiBuilderMessageRenderRows = (messages: AiBuilderMessage[]): AiBuilderMessageRenderRow[] => {
  const rows: AiBuilderMessageRenderRow[] = [];
  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index];
    if (message.kind === 'progress') {
      const nextMessage = messages[index + 1];
      if (nextMessage?.kind === 'assistant') {
        rows.push({
          id: `${message.id}:${nextMessage.id}`,
          type: 'assistantGroup',
          variant: 'assistantWithProgress',
          progressMessage: message,
          assistantMessage: nextMessage
        });
        index += 1;
        continue;
      }
      rows.push({
        id: message.id,
        type: 'assistantGroup',
        variant: 'progressOnly',
        progressMessage: message
      });
      continue;
    }
    if (message.kind === 'assistant') {
      rows.push({
        id: message.id,
        type: 'assistantGroup',
        variant: 'assistantOnly',
        assistantMessage: message
      });
      continue;
    }
    rows.push({
      id: message.id,
      type: 'standaloneMessage',
      message
    });
  }
  return rows;
};
const AiBuilderMessageRow = memo(function AiBuilderMessageRow(props: AiBuilderMessageRenderRow & {
  streamingAssistantId: string | null;
}) {
  if (props.type === 'assistantGroup') {
    if (props.variant === 'progressOnly') {
      return <AiBuilderProgressMessage message={props.progressMessage} />;
    }
    return <AiBuilderAssistantMessage message={props.assistantMessage} progressMessage={props.variant === 'assistantWithProgress' ? props.progressMessage : undefined} isStreaming={props.assistantMessage.id === props.streamingAssistantId} />;
  }
  const message = props.message;
  switch (message.kind) {
    case 'user':
      return <AiBuilderUserMessage message={message} />;
    case 'choice':
      return <AiBuilderChoiceMessage message={message} />;
    case 'confirm':
      return <AiBuilderConfirmMessage message={message} />;
    case 'system':
      return <AiBuilderSystemMessage message={message} />;
  }
});